import { useUserData } from '@conteg/auth';
import {
  Button,
  ConfirmModal,
  ErrorDetailInfo,
  Flex,
  Loading,
  showAlertModalError,
} from '@conteg/ui';
import Notification from 'components/notification/notification';
import { CUSTOM_AUTH_TOKEN } from 'config';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PickupReservationDtoInput,
  StorageUnitDocument,
  usePickupReservationMutation,
  useStorageUnitQuery,
} from 'types/generated/graphql';
import { usePoint } from 'utils/device/device-store';
import { resolveTwoFactorErrorKey } from 'utils/errors/resolve-two-factor-error-key';
import { useInvalidateQueries } from 'utils/hooks/use-ivalidate-queries';

type ConfirmPickupReservationProps = {
  storageUnitId: string;
  storageUnitCount: number;
  /**
   * Ask for the confirmation straight away instead of waiting for the button. The assisted open does not
   * record the pickup (OXIT-196), so right after it the operator is asked whether the customer took the
   * parcel; declining keeps the button for later.
   */
  askImmediately?: boolean;
};

const ConfirmPickupReservation = ({
  storageUnitId,
  storageUnitCount,
  askImmediately = false,
}: ConfirmPickupReservationProps) => {
  const { t } = useTranslation();
  const { pointId } = usePoint();
  const userData = useUserData(CUSTOM_AUTH_TOKEN);
  const userId = userData?.userId;

  const {
    data,
    isLoading,
    error: storageUnitError,
  } = useStorageUnitQuery(
    {
      pointId: pointId as string,
      storageUnitId,
    },
    {
      enabled: !!pointId && !!storageUnitId,
      select: (data) => ({
        reservationId: data.storageUnit.allocation?.reservation?.id,
        tenantSubjectId:
          data.storageUnit.allocation?.reservation?.tenantSubjectId,
        customerPinCredentialId:
          data.storageUnit.allocation?.reservation?.customerPinCredentials?.[0]
            ?.id,
      }),
    }
  );

  const reservationId = data?.reservationId;
  const tenantSubjectId = data?.tenantSubjectId;
  const customerPinCredentialId = data?.customerPinCredentialId;

  const invalidate = useInvalidateQueries();

  const { mutate: pickupReservation, isPending } =
    usePickupReservationMutation();

  const [pickupResult, setPickupResult] = useState<string>('');
  const [displayButton, setDisplayButton] = useState<boolean>(true);
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(askImmediately);

  const handlePickupReservation = () => {
    if (!reservationId) {
      setPickupResult('Page.StorageUnit.Otp.EndReservation.ReservationError');
      return;
    }

    const input: PickupReservationDtoInput = {
      reservationId,
      pointId: pointId as string,
      tenantSubjectId,
      customerPinCredentialId,
      userId,
    };

    pickupReservation(
      {
        input,
      },
      {
        onSuccess: () => {
          setPickupResult('Page.StorageUnit.Pickup.Success');
          setDisplayButton(false);
          invalidate([StorageUnitDocument]);
        },
        onError: (error) =>
          showAlertModalError(
            t(resolveTwoFactorErrorKey(error, 'Page.StorageUnit.Pickup.Error')),
            error
          ),
      }
    );
  };

  const isSingular = storageUnitCount === 1;
  const buttonLabel = isSingular
    ? 'Page.StorageUnit.Pickup.Button.Singular'
    : 'Page.StorageUnit.Pickup.Button.Plural';
  const confirmTitle = isSingular
    ? 'Page.StorageUnit.Pickup.ConfirmTitle.Singular'
    : 'Page.StorageUnit.Pickup.ConfirmTitle.Plural';
  const confirmDescription = isSingular
    ? 'Page.StorageUnit.Pickup.ConfirmDescription.Singular'
    : 'Page.StorageUnit.Pickup.ConfirmDescription.Plural';

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center">
        <Loading text={t('Loading')} />
      </Flex>
    );
  }

  if (storageUnitError) {
    return (
      <ErrorDetailInfo
        title={t('Error.Error.LoadStorageUnitDetailError')}
        error={storageUnitError}
      />
    );
  }

  return (
    <Flex flexDirection="column" gap="2rem" alignItems="center" mt="4rem">
      {pickupResult ? (
        <Notification
          testId="pickup-reservation-notification"
          message={pickupResult}
        />
      ) : null}
      {displayButton ? (
        <Flex gap="2rem" justifyContent="center" alignItems="center">
          <Button
            testId="confirm-pickup-reservation-button"
            onClick={() => setConfirmOpen(true)}
            variant="primary"
            size="xl"
            title={t(buttonLabel, { count: storageUnitCount })}
            disabled={isPending}
          />
        </Flex>
      ) : null}
      <ConfirmModal
        testId="confirm-pickup-reservation"
        isOpen={displayButton && isConfirmOpen}
        modalTitle={t(confirmTitle, { count: storageUnitCount })}
        modalDescription={t(confirmDescription, { count: storageUnitCount })}
        confirm={{
          text: t('Page.StorageUnit.Pickup.ConfirmButton'),
          variant: 'primary',
        }}
        cancelText={t('Page.StorageUnit.Pickup.CancelButton')}
        zIndexOffset={999}
        isLoading={isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          handlePickupReservation();
        }}
      />
    </Flex>
  );
};

export default ConfirmPickupReservation;
